import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCatalog } from "@/lib/useCatalog";
import { api, apiError } from "@/lib/api";
import { MODE_META, STATUS_ORDER, STATUS_META } from "@/lib/pricing";
import { isNewOrder } from "@/lib/orderDetails";
import { Navbar } from "@/components/Navbar";
import { StatusBadge } from "@/components/StatusTracker";
import { OrderDetailsEditor } from "@/components/admin/OrderDetailsEditor";
import { OrderItemsEditor } from "@/components/admin/OrderItemsEditor";
import { OrderPaymentsPanel } from "@/components/admin/OrderPaymentsPanel";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const StatusControls = ({ order, onChange }) => (
  <div className="mt-5">
    {order.status === "placed" ? (
      <button data-testid="admin-mark-received" onClick={() => onChange("received")}
        className="flex w-full items-center justify-center gap-2 rounded-2xl bg-sugar-ink py-4 font-display text-lg font-extrabold text-yellow-300 transition-transform hover:scale-[1.01]">
        <CheckCircle2 size={22} /> Mark received
      </button>
    ) : (
      <div className="flex flex-wrap gap-2">
        {STATUS_ORDER.map((s) => (
          <button key={s} data-testid={`admin-set-status-${s}`} onClick={() => onChange(s)} disabled={order.status === s}
            className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${order.status === s ? "text-white" : "border border-sugar-border bg-white text-sugar-ink2 hover:border-sugar-pink hover:text-sugar-pink"}`}
            style={order.status === s ? { background: STATUS_META[s].color } : {}}>
            {STATUS_META[s].label}
          </button>
        ))}
      </div>
    )}
    <p className="mt-2 text-xs text-sugar-ink2">{order.status === "placed" ? "Lets the customer know you've seen their order." : "The customer sees each step on their order tracker."}</p>
  </div>
);

export default function AdminOrder() {
  const { id } = useParams();
  const { user, checked } = useAuth();
  const navigate = useNavigate();
  const catalog = useCatalog();
  const [order, setOrder] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (checked && !user) { navigate("/login", { state: { next: `/admin/orders/${id}` } }); return; }
    if (checked && user && user.role !== "admin") { navigate("/"); return; }
    if (user?.role === "admin") api.get(`/orders/${id}`).then(({ data }) => setOrder(data)).catch(() => setMissing(true));
  }, [checked, user, id, navigate]);

  const changeStatus = async (status) => {
    try {
      const { data } = await api.patch(`/admin/orders/${id}/status`, { status });
      setOrder((o) => ({ ...o, ...data }));
      toast.success(`Marked as ${STATUS_META[status].label}${["confirmed", "preparing", "on_route", "delivered"].includes(status) ? " · customer emailed" : ""}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saved = (data) => setOrder((o) => ({ ...o, ...data }));

  if (missing) return <div className="App"><Navbar /><div className="py-24 text-center text-sugar-ink2">Order not found.</div></div>;
  if (!order) return <div className="App"><Navbar /><div className="py-24 text-center text-sugar-ink2">Loading…</div></div>;
  const fresh = isNewOrder(order);

  return (
    <div className="App min-h-screen bg-sugar-bg"><Navbar />
      <div className="mx-auto max-w-5xl px-5 py-8 lg:px-8">
        <button data-testid="admin-order-back" onClick={() => navigate("/admin")} className="mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-sugar-ink2 hover:text-sugar-pink"><ArrowLeft size={16} /> Dashboard</button>
        <div className={`rounded-2xl border-2 p-5 sm:p-6 ${fresh ? "border-yellow-400 bg-yellow-300" : "border-sugar-border bg-white"}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 data-testid="admin-order-title" className="font-display text-2xl font-extrabold text-sugar-ink">Order #{order.id.slice(-6).toUpperCase()}</h1>
            <StatusBadge status={order.status} />
          </div>
          <p className="mt-1 text-sm text-sugar-ink2">{MODE_META[order.mode]?.label} · for {order.fulfillment_date} · placed {new Date(order.created_at).toLocaleString()}</p>
          <StatusControls order={order} onChange={changeStatus} />
        </div>
        <div className="mt-6 grid gap-6 lg:grid-cols-[1.3fr_1fr]">
          <div className="space-y-6">
            <OrderItemsEditor order={order} catalog={catalog} onSaved={saved} />
            <OrderDetailsEditor order={order} onSaved={saved} />
          </div>
          <OrderPaymentsPanel order={order} catalog={catalog} onSaved={saved} />
        </div>
      </div>
    </div>
  );
}
